import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Box, ButtonGroup } from '@mui/material';
import { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  StyledButton,
  StyledButtonContent,
  StyledButtonLabel,
  StyledLeadingIcon,
  StyledMenu,
  StyledMenuItem,
  StyledTrailingIcon,
} from './SplitButtonMenuTsoPqStyle';

export type ButtonConfig = {
  id: string;
  label: string;
  customWidth?: string;
  options: string[];
};

const SplitButtonMenuTsoPq = ({
  buttonsConfig,
  maximumActiveOptions = 2,
  defaultSelectedOptions,
  handleChange,
}: {
  buttonsConfig: ButtonConfig[];
  maximumActiveOptions?: number;
  defaultSelectedOptions?: { [key: string]: string };
  handleChange: (values: { [key: string]: any }) => void;
}) => {
  const [anchorEl, setAnchorEl] = useState();
  const [activeMenuId, setActiveMenuId] = useState<string | undefined>();
  const [menus, setMenus] = useState<{ [key: string]: any }>({});

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleMenuClick = (event: any, id: string) => {
    if (activeMenuId === id) {
      handleClose();
      return;
    }

    setAnchorEl(event.currentTarget);
    setActiveMenuId(id);
  };

  const handleMenuItemClick = (id: string, option: string) => {
    const activeButtons = Object.values(menus);
    if (activeButtons.filter((value) => value).length >= 2 && !menus[id]) {
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: `${t('general.error.maximumSelectedOptions')} ${maximumActiveOptions}`,
        })
      );
      return;
    }

    setMenus((prev) => {
      const nextValues = { ...prev, [id]: prev[id] === option ? undefined : option };
      handleChange(nextValues);
      return nextValues;
    });
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(undefined);
    setActiveMenuId(undefined);
  };

  useEffect(() => {
    if (defaultSelectedOptions) {
      setMenus(defaultSelectedOptions);
    }
  }, []);

  return (
    <ButtonGroup variant="outlined">
      {buttonsConfig.map((buttonConfig: ButtonConfig) => (
        <Box key={buttonConfig.id}>
          <StyledButton
            active={!!menus[buttonConfig.id]}
            color="error"
            onClick={(event) => handleMenuClick(event, buttonConfig.id)}
            customWidth={buttonConfig.customWidth}
          >
            <StyledButtonContent>
              <StyledLeadingIcon>
                {menus[buttonConfig.id] && <FontAwesomeIcon icon={faCheck} style={{ fontSize: '14px' }} />}
              </StyledLeadingIcon>
              <StyledButtonLabel>
                <TypographyTsoPq typographyType={'pageSubtitleSM'}>
                  {menus[buttonConfig.id] ?? buttonConfig.label}
                </TypographyTsoPq>
              </StyledButtonLabel>
              <StyledTrailingIcon>
                <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '14px' }} />
              </StyledTrailingIcon>
            </StyledButtonContent>
          </StyledButton>
          <StyledMenu
            anchorEl={activeMenuId === buttonConfig.id ? anchorEl : undefined}
            open={activeMenuId === buttonConfig.id}
            onClose={handleClose}
            customWidth={buttonConfig.customWidth}
          >
            {buttonConfig.options.map((option) => (
              <StyledMenuItem
                key={option}
                selected={menus[buttonConfig.id] === option}
                onClick={() => handleMenuItemClick(buttonConfig.id, option)}
              >
                <TypographyTsoPq typographyType={'pageSubtitleSM'}>{option}</TypographyTsoPq>
              </StyledMenuItem>
            ))}
          </StyledMenu>
        </Box>
      ))}
    </ButtonGroup>
  );
};

export default SplitButtonMenuTsoPq;
